import download from 'downloadjs';
import moment from 'moment';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { theme, createStandaloneToast } from '@frontend/components';

import { getGroupId } from 'store/session/selectors';

import { getErrorMessage } from 'utils/stringUtils';

import * as actions from './actions';
import service from './service';

const { toast } = createStandaloneToast({ theme });
const types = actions.types;

export default function* root() {
  yield takeEvery(types.CREATE_REQUEST, watchCreateProcess);
  yield takeLatest(types.ACCOUNT_DOWNLOAD_REQUEST, watchDownloadAccountConfig);
  yield takeLatest(types.ACCOUNT_VALIDATE_REQUEST, watchValidateAccountConfig);
  yield takeLatest(types.ACCOUNT_UPLOAD_REQUEST, watchUploadAccountConfig);
}

export function* initProcessListPage() {
  const groupId = yield select(getGroupId);
  const processes = yield call(service.list, groupId);
  yield put(actions.init(processes));
}

function* watchCreateProcess(action) {
  const groupId = yield select(getGroupId);
  try {
    const process = yield call(service.create, groupId, action.payload);
    yield put(actions.createProcess.success(process));
  } catch (error) {
    yield put(actions.createProcess.error(error));
    toast({
      title: 'Failed to Create Process',
      status: 'error',
      description: getErrorMessage(error),
      duration: 2000,
    });
  }
}

function* watchDownloadAccountConfig({ payload }) {
  const { criticalFieldsOnly, writeVariablesToCsv } = payload;
  const groupId = yield select(getGroupId);

  try {
    const response = yield call(service.downloadAccountYaml, groupId, {
      criticalFieldsOnly,
      writeVariablesToCsv,
    });
    yield put(actions.accountDownload.success());
    yield put(actions.accountDownloadModal.close());
    yield call(toast, {
      status: 'info',
      title: 'Download started.',
    });
    const blob = new Blob([response], {
      type: 'application/zip',
    });
    yield call(
      download,
      blob,
      `${groupId}-account-config-${moment().format('MM-DD-HH:mm')}.zip`,
    );
  } catch (error) {
    yield put(actions.accountDownload.error(error));
  }
}

function* watchValidateAccountConfig({ payload }) {
  const groupId = yield select(getGroupId);

  try {
    yield call(service.validateAccountYaml, groupId, payload);
    yield put(actions.accountValidate.success());
    yield put(actions.accountValidateModal.close());
  } catch (error) {
    yield put(actions.accountValidateModal.close());
    yield put(actions.accountValidate.error(error));
    if (typeof error.originalResponse === 'string') {
      yield call(toast, {
        status: 'error',
        title: 'Account Config Invalid',
        description: 'Check error report for details',
        duration: 30000,
      });
      const blob = new Blob([error.originalResponse], {
        type: 'text/plain',
      });
      yield call(download, blob, `${groupId}-error-report.yaml`);
    } else {
      yield call(toast, {
        title: 'Account Config Validation Failed',
        description: getErrorMessage(error),
        status: 'error',
        duration: 30000,
      });
    }
  }
}

function* watchUploadAccountConfig({ payload }) {
  const groupId = yield select(getGroupId);

  try {
    const response = yield call(service.uploadAccountYaml, groupId, payload);
    const processes = yield call(service.list, groupId);
    yield put(actions.init(processes));
    yield put(actions.accountUpload.success());
    yield put(actions.accountUploadModal.close());
    yield call(toast, {
      status: 'info',
      title: 'Upload started.',
      duration: 2000,
    });
    const blob = new Blob([response], {
      type: 'application/zip',
    });
    yield call(
      download,
      blob,
      `${groupId}-account-config-${moment().format('MM-DD-HH:mm')}.zip`,
    );
  } catch (error) {
    yield put(actions.accountUploadModal.close());
    yield put(actions.accountUpload.error(error));
    if (typeof error.originalResponse === 'string') {
      yield call(toast, {
        title: 'Account Config Import Failed',
        description: 'Check error report for details',
        status: 'error',
        duration: 30000,
      });
      const blob = new Blob([error.originalResponse], {
        type: 'text/plain',
      });
      yield call(download, blob, `${groupId}-error-report.yaml`);
    } else {
      yield call(toast, {
        title: 'Account Config Import Failed',
        description: getErrorMessage(error),
        status: 'error',
        duration: 30000,
      });
    }
  }
}
